import { createContext, useContext, useEffect, useState } from "react";
import {
  applyUiTheme,
  readUiTheme,
  storeUiTheme,
  type UiTheme,
} from "@/lib/ui-theme";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  /** Light/dark axis (`.dark` on <html>). */
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Visual theme axis (`data-theme` on <html>): Classic or Lilac. */
  uiTheme: UiTheme;
  setUiTheme: (theme: UiTheme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  uiTheme: "lilac",
  setUiTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );
  const [uiTheme, setUiThemeState] = useState<UiTheme>(() => readUiTheme());

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  // The inline script in index.html already set `data-theme` before first paint; this keeps
  // it in sync with later changes.
  useEffect(() => {
    applyUiTheme(uiTheme);
  }, [uiTheme]);

  const value: ThemeProviderState = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    uiTheme,
    setUiTheme: (next: UiTheme) => {
      storeUiTheme(next);
      setUiThemeState(next);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
